import {
  PARTNER_CATEGORIES,
  PARTNER_KINDS,
  PARTNER_KIND_LABEL,
  laAdsense,
  timDoiTacTrungTen,
} from '@qlk/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { EmptyState, MAU_DU_LIEU_MAC_DINH, Nut, PageHeader } from '../../../ui/index.js';
import { useCapNhatDoiTac, useDanhBaDoiTac, useMotDoiTac, useTaoDoiTac } from '../api.js';
import { O, OChonTruong } from '../../../ui/index.js';

const NHAN_NHOM: Record<(typeof PARTNER_CATEGORIES)[number], string> = {
  general: 'Chung',
  music: 'Nhạc',
};

export function TrangFormDoiTac() {
  const { id } = useParams();
  const dieuHuong = useNavigate();
  const dangSua = Boolean(id);

  const doiTac = useMotDoiTac(id);
  const danhBa = useDanhBaDoiTac();
  const tao = useTaoDoiTac();
  const capNhat = useCapNhatDoiTac(id ?? '');

  const [form, datForm] = useState({
    name: '',
    kind: 'mcn' as (typeof PARTNER_KINDS)[number],
    category: 'general' as (typeof PARTNER_CATEGORIES)[number],
    logoColor: MAU_DU_LIEU_MAC_DINH,
    contactName: '',
    contactEmail: '',
    notes: '',
    publisherCode: '',
  });
  const [daNap, datDaNap] = useState(false);
  const [xacNhanTrung, datXacNhanTrung] = useState(false);

  if (dangSua && doiTac.data && !daNap) {
    datForm({
      name: doiTac.data.name,
      kind: doiTac.data.kind,
      category: doiTac.data.category,
      logoColor: doiTac.data.logoColor,
      contactName: doiTac.data.contactName ?? '',
      contactEmail: doiTac.data.contactEmail ?? '',
      notes: doiTac.data.notes ?? '',
      publisherCode: doiTac.data.publisherCode ?? '',
    });
    datDaNap(true);
  }

  const dangGui = tao.isPending || capNhat.isPending;
  const loi = tao.error ?? capNhat.error;
  const adsense = laAdsense(form.kind);

  /*
   * Trùng tên với một đối tác **của cả công ty**, kể cả đối tác người đang xem
   * không được đọc chi tiết (danh bạ trả tên mọi đối tác).
   *
   * So trùng bằng `timDoiTacTrungTen` — đúng hàm máy chủ dùng, nên cảnh báo ở
   * đây và lỗi 409 ở kia không bao giờ lệch nhau.
   */
  const trungTen = timDoiTacTrungTen(danhBa.data ?? [], form.name, id);
  const chanVaoTrung = trungTen !== null && !xacNhanTrung;

  function gui(): void {
    const than = {
      name: form.name.trim(),
      kind: form.kind,
      category: form.category,
      logoColor: form.logoColor,
      contactName: form.contactName.trim() || null,
      contactEmail: form.contactEmail.trim() || null,
      notes: form.notes.trim() || null,
      // Mã nhà xuất bản chỉ thuộc tài khoản AdSense; API từ chối nếu gửi cho MCN.
      publisherCode: adsense ? form.publisherCode.trim() || null : null,
      ...(xacNhanTrung ? { xacNhanTrungTen: true } : {}),
    };

    if (dangSua) {
      capNhat.mutate(than, {
        onSuccess: () => {
          void dieuHuong(`/network/${id ?? ''}`);
        },
      });
    } else {
      tao.mutate(than, {
        onSuccess: (d) => {
          void dieuHuong(`/network/${d.id}`);
        },
      });
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={['VẬN HÀNH', 'Network (MCN)']}
        title={dangSua ? 'Sửa đối tác thanh toán' : 'Thêm hợp đồng Network'}
        description={
          adsense
            ? 'Một tài khoản AdSense của công ty. Không có tỷ lệ chia; thuế Mỹ và ngưỡng chi trả khai ở tab "Cài đặt AdSense" trong trang chi tiết.'
            : 'Thông tin đối tác. Tỷ lệ chia và kỳ thanh toán khai ở hợp đồng bên trong trang chi tiết.'
        }
      />

      <form
        className="max-w-2xl rounded-the border border-vien bg-be-mat p-6"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <O
            label="Tên đối tác"
            value={form.name}
            onChange={(v) => {
              datForm({ ...form, name: v });
            }}
            required
            className="sm:col-span-2"
          />

          <OChonTruong
            label="Loại"
            value={form.kind}
            onChange={(v) => {
              datForm({ ...form, kind: v as (typeof PARTNER_KINDS)[number] });
            }}
            options={PARTNER_KINDS.map((k) => ({ value: k, label: PARTNER_KIND_LABEL[k] }))}
          />
          <OChonTruong
            label="Nhóm"
            value={form.category}
            onChange={(v) => {
              datForm({ ...form, category: v as (typeof PARTNER_CATEGORIES)[number] });
            }}
            options={PARTNER_CATEGORIES.map((c) => ({ value: c, label: NHAN_NHOM[c] }))}
          />

          {adsense && (
            <O
              label="Mã nhà xuất bản"
              value={form.publisherCode}
              onChange={(v) => {
                datForm({ ...form, publisherCode: v });
              }}
              moTa="Dạng pub- kèm 16 chữ số, lấy ở AdSense → Tài khoản."
              className="sm:col-span-2"
            />
          )}

          <O
            label="Màu logo"
            type="color"
            value={form.logoColor}
            onChange={(v) => {
              datForm({ ...form, logoColor: v });
            }}
          />
          <O
            label="Người liên hệ"
            value={form.contactName}
            onChange={(v) => {
              datForm({ ...form, contactName: v });
            }}
          />
          <O
            label="Email liên hệ"
            type="email"
            value={form.contactEmail}
            onChange={(v) => {
              datForm({ ...form, contactEmail: v });
            }}
            className="sm:col-span-2"
          />
          <O
            label="Ghi chú"
            value={form.notes}
            onChange={(v) => {
              datForm({ ...form, notes: v });
            }}
            nhieuDong
            className="sm:col-span-2"
          />
        </div>

        {trungTen && (
          <div
            role="status"
            className="mt-4 rounded-[10px] bg-warn-nen px-4 py-3 text-co-ban text-warn-chu"
          >
            <p className="font-semibold">
              Công ty đã có đối tác “{trungTen.name}” — tên chỉ khác hoa thường hoặc dấu cách.
            </p>
            <p className="mt-1">
              Gần như chắc chắn đây là cùng một Network. Tạo thêm một dòng nữa là kênh hai bên đi
              hai hợp đồng, hai tỷ lệ chia và hai mức thuế Mỹ, rồi sao kê không bao giờ khớp.{' '}
              <Link
                to={`/network/${trungTen.id}`}
                className="font-semibold text-nhan hover:underline"
              >
                Mở “{trungTen.name}” →
              </Link>
            </p>
            <label className="vung-bam mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={xacNhanTrung}
                onChange={(e) => {
                  datXacNhanTrung(e.target.checked);
                }}
                className="h-4 w-4"
              />
              Vẫn tạo đối tác mới — đây là hai bên khác nhau
            </label>
          </div>
        )}

        {loi && (
          <div className="mt-4 rounded-[10px] bg-bad-nen px-4 py-3 text-co-ban text-bad-chu">
            {loi.message}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Nut kieu="chinh" type="submit" dangChay={dangGui} disabled={chanVaoTrung}>
            {dangSua ? 'Lưu thay đổi' : 'Tạo đối tác'}
          </Nut>
          <Nut
            onClick={() => {
              void dieuHuong(-1);
            }}
          >
            Huỷ
          </Nut>
        </div>
      </form>

      {dangSua && doiTac.error && (
        <div className="mt-4 rounded-the border border-vien bg-be-mat">
          <EmptyState loi title="Không mở được đối tác" description={doiTac.error.message} />
        </div>
      )}
    </>
  );
}
